import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Dropdown, Modal, message, InputNumber, Switch, Space } from 'antd';
import { 
  SettingOutlined, 
  CheckCircleOutlined,
  MoreOutlined 
} from '@ant-design/icons';
import StationService from '../../services/StationService';
import SettingsService from '../../services/SettingsService';
import {
  StatusButton,
  StatusDot,
  StatusText,
  TimerDisplay,
  ConfigRow,
  ConfigLabel
} from './styled';

const STATUS_TYPES = {
  AVAILABLE: 'available',
  SCREENING: 'screening',
  CLEANING: 'cleaning'
};

const STATUS_CONFIG = {
  [STATUS_TYPES.AVAILABLE]: {
    color: '#52c41a',
    text: 'Available'
  },
  [STATUS_TYPES.SCREENING]: {
    color: '#ff4d4f',
    text: 'Screening'
  },
  [STATUS_TYPES.CLEANING]: {
    color: '#faad14',
    text: 'Cleaning'
  }
};

const StatusIndicator = forwardRef(({ 
  bookingId, // Changed from eventId to bookingId
  onSettingsChange,
  initialSettings = {
    timerEnabled: true,
    timerDuration: 2, // minutes
    yellowThreshold: 80 // percentage
  }
}, ref) => {
  const [stationStatus, setStationStatus] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [configVisible, setConfigVisible] = useState(false);
  const [settings, setSettings] = useState(initialSettings);
  const [loading, setLoading] = useState(true);

  // Load station status and settings on mount or when bookingId changes
  useEffect(() => {
    if (bookingId) {
      loadStationStatus();
      loadSettings();
      
      // Set up polling for status updates
      const interval = setInterval(loadStationStatus, 30000); // Poll every 30 seconds
      return () => clearInterval(interval);
    }
  }, [bookingId]); // Add bookingId as dependency

  // Update settings when initialSettings change
  useEffect(() => {
    setSettings(initialSettings);
  }, [initialSettings]);

  // Auto cleanup function (must be defined before timer effect)
  const handleAutoCleanup = useCallback(async () => {
    try {
      if (!bookingId) {
        console.log('No bookingId provided, cannot auto cleanup');
        return;
      }
      
      const response = await StationService.autoCleanup(bookingId);
      if (response.success) {
        setStationStatus(response.data);
      }
    } catch (error) {
      console.error('Error during auto cleanup:', error);
    }
  }, [bookingId]);

  // Timer countdown effect
  useEffect(() => {
    let interval;
    
    if (stationStatus?.timerEndTime) {
      const updateTimer = () => {
        const endTime = new Date(stationStatus.timerEndTime);
        const now = new Date();
        const remaining = Math.max(0, Math.floor((endTime - now) / 1000));
        
        setTimeRemaining(remaining);
        
        if (remaining <= 0) {
          // Timer expired, auto cleanup
          handleAutoCleanup();
        }
      };
      
      updateTimer(); // Initial update
      interval = setInterval(updateTimer, 1000);
    } else {
      setTimeRemaining(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [stationStatus?.timerEndTime, handleAutoCleanup]);

  // Calculate current status based on timer progress
  const getCurrentStatus = () => {
    if (!stationStatus) return STATUS_TYPES.AVAILABLE;
    
    // If no timer, return the stored status
    if (!stationStatus.timerEndTime || timeRemaining === 0) {
      // Map backend status strings to STATUS_TYPES constants
      switch (stationStatus.status) {
        case 'screening':
          return STATUS_TYPES.AVAILABLE;
        case 'cleaning':
          return STATUS_TYPES.CLEANING;
        case 'available':
          return STATUS_TYPES.AVAILABLE;
        default:
          return STATUS_TYPES.AVAILABLE;
      }
    }
    
    // Calculate progress for red -> yellow transition
    if (stationStatus.status === 'screening' && stationStatus.screeningStartTime) {
      const startTime = new Date(stationStatus.screeningStartTime);
      const endTime = new Date(stationStatus.timerEndTime);
      const totalDuration = endTime.getTime() - startTime.getTime();
      const elapsed = Date.now() - startTime.getTime();
      const elapsedPercentage = (elapsed / totalDuration) * 100;
      
      // Check if we've passed the yellow threshold
      if (elapsedPercentage >= (settings.yellowThreshold || 80)) {
        return STATUS_TYPES.CLEANING; // Yellow phase
      } else {
        return STATUS_TYPES.SCREENING; // Red phase
      }
    }
    
    // Map any remaining status strings to STATUS_TYPES constants
    switch (stationStatus.status) {
      case 'screening':
        return STATUS_TYPES.SCREENING;
      case 'cleaning':
        return STATUS_TYPES.CLEANING;
      case 'available':
        return STATUS_TYPES.AVAILABLE;
      default:
        return STATUS_TYPES.AVAILABLE;
    }
  };

  const loadStationStatus = async () => {
    try {
      if (!bookingId) {
        console.log('No bookingId provided, skipping station status load');
        setLoading(false);
        return;
      }
      
      const response = await StationService.getStationStatus(bookingId);
      if (response.success) {
        setStationStatus(response.data);
      } else {
        console.error('Failed to load station status:', response.error);
        // Set a default "available" status when API fails
        setStationStatus({
          status: 'available',
          stationName: 'Station',
          bookingId: bookingId
        });
      }
    } catch (error) {
      console.error('Error loading station status:', error);
      // Set a default "available" status when API fails
      setStationStatus({
        status: 'available',
        stationName: 'Station',
        bookingId: bookingId
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await SettingsService.getQueueSettings();
      if (response.success && response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const triggerScreeningComplete = useCallback(async () => {
    try {
      if (!bookingId) {
        console.log('No bookingId provided, cannot complete screening');
        message.error('Booking ID required');
        return;
      }
      
      const response = await StationService.completeScreening(bookingId);
      if (response.success) {
        setStationStatus(response.data);
        message.success('Screening completed - cleanup timer started');
      } else {
        message.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error completing screening:', error);
      message.error('Failed to update status');
    }
  }, [bookingId]);

  const forceAvailable = useCallback(async () => {
    try {
      if (!bookingId) {
        console.log('No bookingId provided, cannot mark available');
        message.error('Booking ID required');
        return;
      }
      
      const response = await StationService.markAvailable(bookingId);
      if (response.success) {
        setStationStatus(response.data);
        message.info('Station marked as available');
      }
    } catch (error) {
      console.error('Error marking available:', error);
      message.error('Failed to update status');
    }
  }, [bookingId]);

  const handleSettingsUpdate = useCallback(async (newSettings) => {
    try {
      const response = await SettingsService.updateQueueSettings(newSettings);
      if (response.success) {
        setSettings(newSettings);
        setConfigVisible(false);
        
        if (onSettingsChange) {
          onSettingsChange(newSettings);
        }
        
        message.success('Settings updated successfully');
      } else {
        message.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      message.error('Failed to save settings');
    }
  }, [onSettingsChange]);

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    triggerScreeningComplete
  }), [triggerScreeningComplete]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusDisplay = () => {
    if (!stationStatus) return { color: '#d9d9d9', text: 'Loading...' };
    
    const currentStatus = getCurrentStatus();
    const config = STATUS_CONFIG[currentStatus] || STATUS_CONFIG[STATUS_TYPES.AVAILABLE];
    return {
      ...config,
      showTimer: timeRemaining > 0 && currentStatus !== STATUS_TYPES.AVAILABLE
    };
  };

  // Don't render StatusIndicator if no event is selected
  if (!bookingId) {
    return null;
  }

  const dropdownMenuItems = [
    ...(getCurrentStatus() !== STATUS_TYPES.AVAILABLE
      ? [{
          key: 'reset',
          label: (<><CheckCircleOutlined /> Mark Available</>),
          onClick: forceAvailable,
        }]
      : []),
    {
      key: 'settings',
      label: (<><SettingOutlined /> Timer Settings</>),
      onClick: () => setConfigVisible(true),
    },
  ];

  const statusDisplay = getStatusDisplay();

  if (loading) {
    return (
      <StatusButton disabled>
        <Space size={8}>
          <StatusDot color="#d9d9d9" />
          <StatusText>Loading...</StatusText>
        </Space>
      </StatusButton>
    );
  }

  return (
    <>
      <Dropdown menu={{ items: dropdownMenuItems }} trigger={['click']} placement="bottomRight">
        <StatusButton>
          <Space size={8}>
            <StatusDot color={statusDisplay.color} />
            <StatusText>{statusDisplay.text}</StatusText>
            {statusDisplay.showTimer && (
              <TimerDisplay>{formatTime(timeRemaining)}</TimerDisplay>
            )}
            <MoreOutlined />
          </Space>
        </StatusButton>
      </Dropdown>

      <Modal
        title="Timer Settings"
        open={configVisible}
        onCancel={() => setConfigVisible(false)}
        onOk={() => {
          handleSettingsUpdate(settings);
        }}
        okText="Save Settings"
        width={500}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <ConfigRow>
            <ConfigLabel>Enable Timer</ConfigLabel>
            <Switch
              checked={settings.timerEnabled}
              onChange={(checked) => setSettings(prev => ({ ...prev, timerEnabled: checked }))}
            />
          </ConfigRow>

          <ConfigRow>
            <ConfigLabel>Timer Duration (minutes)</ConfigLabel>
            <InputNumber
              min={0}
              max={10}
              value={settings.timerDuration}
              onChange={(value) => setSettings(prev => ({ ...prev, timerDuration: value }))}
              disabled={!settings.timerEnabled}
              style={{ width: '100%' }}
            />
          </ConfigRow>

          <ConfigRow>
            <ConfigLabel>
              Cleaning Phase Threshold (%)
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                When to switch from red (screening) to yellow (cleaning)
              </div>
            </ConfigLabel>
            <InputNumber
              min={50}
              max={95}
              value={settings.yellowThreshold}
              onChange={(value) => setSettings(prev => ({ ...prev, yellowThreshold: value }))}
              disabled={!settings.timerEnabled}
              formatter={value => `${value}%`}
              parser={value => value.replace('%', '')}
              style={{ width: '100%' }}
            />
          </ConfigRow>

          <div style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '12px', 
            borderRadius: '6px', 
            fontSize: '14px',
            marginTop: '16px'
          }}>
            <strong>How it works:</strong>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li><span style={{ color: '#ff4d4f' }}>●</span> Red: Active screening in progress</li>
              <li><span style={{ color: '#faad14' }}>●</span> Yellow: Cleaning/setup phase</li>
              <li><span style={{ color: '#52c41a' }}>●</span> Green: Available for next patient</li>
            </ul>
          </div>
        </div>
      </Modal>
    </>
  );
});

StatusIndicator.displayName = 'StatusIndicator';

export default StatusIndicator;
